export interface NotificationData {
    type: string;
    chatMessage?: string;
    customerId?: string | number;
    eventId?: string | number;
    proformaUuid?: string;
    // add any extra fields you might have in your data payload
}

export interface HandlerOptions {
    data: NotificationData;
    customers?: { id: number }[];
    setSelectedCustomerId: (arg: { id: number; }) => Promise<{ selectedId: any; }>
    history: { push: (url: string) => void };
    ionRouter?: { push: (path: string, direction?: any, action?: any) => void };
    currentLocation?: string;
    addMessage?: (message: any) => void; // Replace 'any' with your ChatMessage type if available
}

/**
 * Handles push notification actions by checking the type and performing the
 * appropriate navigation and state updates.
 */
export const handleNotificationAction = async ({
                                                   data,
                                                   customers,
                                                   setSelectedCustomerId,
                                                   history,
                                                   ionRouter,
                                                   currentLocation,
                                                   addMessage,
                                               }: HandlerOptions) => {

    // A notification tap is an out-of-band event: the on-screen route is incidental,
    // not a real predecessor. Push through the Ionic router with direction 'none' so
    // it records no `pushedByRoute` — otherwise the target page's IonBackButton
    // returns to whatever happened to be showing (e.g. /customer-select) instead of
    // its own backRouterLink/defaultHref. Web has no Ionic router here and instead
    // hard-navigates (window.location), which reboots the stack fresh and is
    // unaffected — so fall back to history.push there.
    const navigate = (path: string) => (ionRouter ? ionRouter.push(path, 'none') : history.push(path));

    switch (data.type) {
        case 'chatMessage': {
            if (data.chatMessage) {
                try {
                    const parsedMessage = JSON.parse(data.chatMessage);
                    if (parsedMessage.customerId) {
                        addMessage && addMessage(parsedMessage);
                        if (customers && customers.some((customer) => customer.id === parsedMessage.customerId)){
                            await setSelectedCustomerId({id: parsedMessage.customerId});
                            if (currentLocation !== '/chat') {
                                navigate('/chat');
                            }
                        }
                    }
                } catch (error) {
                    console.error('Failed to parse chatMessage:', error);
                }
            }
            break;
        }
        case 'shopPaymentComplete': {
            if (data.customerId && data.proformaUuid) {
                const customerId = parseInt(data.customerId as string, 10);
                if (customers && customers.some((customer) => customer.id === customerId)) {
                    await setSelectedCustomerId({id: customerId});
                    const targetUrl = `/payment-return?proformaUuid=${data.proformaUuid}`;
                    if (currentLocation !== targetUrl) {
                        navigate(targetUrl);
                    }
                }
            }
            break;
        }
        case 'eventReminder': {
            if (!data.eventId) {
                console.warn('eventReminder push received without eventId; skipping nav.');
                break;
            }
            if (customers && data.customerId) {
                const customerId = parseInt(data.customerId as string, 10);
                const customerExists = customers.some((customer) => customer.id === customerId);
                if (customerExists) {
                    await setSelectedCustomerId({ id: customerId });
                    const targetUrl = `/events/${data.eventId}`;
                    if (currentLocation !== targetUrl) {
                        navigate(targetUrl);
                    }
                }
            }
            break;
        }
        case 'bookingReminder': {
            if (customers && data.customerId) {
                const customerId = parseInt(data.customerId as string, 10);
                const customerExists = customers.some((customer) => customer.id === customerId);
                if (customerExists) {
                    await setSelectedCustomerId({ id: customerId });
                    if (currentLocation !== '/home') {
                        navigate('/home');
                    }
                }
            }
            break;
        }
        default: {
            // Fallback logic if a customerId is provided in the root of data
            if (customers && data.customerId) {
                const customerId = parseInt(data.customerId as string, 10);
                const customerExists = customers.some((customer) => customer.id === customerId);
                if (customerExists) {
                    await setSelectedCustomerId({id: customerId});
                    if (currentLocation !== '/active-customer') {
                        navigate('/active-customer');
                    }
                }
            }
            break;
        }
    }
};
